/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { calculateHeight, errorMsg, isSmWidth } from '../../../../../Apis/functions';
import { GetMaterialControlById } from '../../../../../reduxToolkit/MaterialControl/MaterialControlApi';
import routes from '../../../../../Routes/routes';
import { FILE_MODEL } from '../../../Visitor/Visitor/Model/Model';
import {
    getEntryAndExitFormByKey,
    getEntryAndExitFormByType,
    getMovementDirectionOptionByValue,
    getMovementDirectionSection,
    getPendingGateEventTrans,
    MATERIAL_CONTROL_DETAIL_MODEL
} from '../../Model/Model';
import { STATUS_MODEL } from '../../../../../enums/statusEnum';
import { USER_TYPE_NAME_MODEL } from '../../../../../enums/userTypeEnum';

/**
 * Detail information of an entry and exit form.
 *
 * Every form type is registered by the material control service, so the detail
 * is read with the same service and the type of the form tells which sections
 * are displayed for it.
 *
 * @returns {Object} Information and handlers of the detail view.
 */
const GeneralEntryExitDetailsApp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { id } = useParams();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission, user } = useSelector((state) => state.authenticatioauthennSlice);
    // A guard carries no permissions, its user type reads and closes the folio at the gate
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    const containerCardRef = useRef(null);
    const containerTableRef = useRef(null);
    const contentCardRef = useRef(null);
    const contentTableRef = useRef(null);
    const [item, setItem] = useState(null);
    const [evidence, setEvidence] = useState([]);
    // The badge of the detail reads the result of the folio with the shape
    // every movement of material control carries, and the blocks the service
    // keeps apart travel with it under the names the view looks for
    const detail = MATERIAL_CONTROL_DETAIL_MODEL.toSet(item);
    const statusId = detail?.dispatchStatusId;

    /**
     * Type of the form being displayed.
     *
     * The type is read from the form itself and falls back to the one selected
     * when the user navigates from the list, because the forms registered
     * before the type was captured do not return it.
     */
    const form = item?.type
        ? getEntryAndExitFormByType(item.type)
        : getEntryAndExitFormByKey(location?.state?.formKey);

    // A dispatched folio is closed, and the gate log it was dispatched with is
    // the one that tells it, so only what the gate reported is read above the
    // detail
    const showBadge = statusId === STATUS_MODEL.DISCREPANCY;
    // The gate is what closes the folio, so while it waits for a movement of it
    // the notice of the one it waits for is read instead of a badge
    const pendingGateTrans = getPendingGateEventTrans(statusId);

    /**
     * Direction of the movement and the complementary section it enables.
     *
     * Both are null for the forms that do not capture a direction, so the view
     * only displays them when the movement has one.
     */
    const movementDirectionOption = getMovementDirectionOptionByValue(item?.movementDirection);
    const movementDirectionSection = getMovementDirectionSection(movementDirectionOption?.key);

    /**
     * Effect: Adjusts dynamic container heights based on screen size
     * and dispatch badge visibility.
     *
     * Behavior:
     * - Skips execution on small screen devices.
     * - Dynamically calculates available height for:
     *   - Main card container
     *   - Table container
     *   - Card content area
     *   - Table content area
     * - Applies different bottom spacing depending on whether
     *   the dispatch badge is visible.
     *
     * The height is measured again once the folio is read, because what it
     * carries above the cards — the dispatch, the denial of the approver or the
     * notice of what the gate still has to register — is the one that says how
     * much room is left for them.
     */
    useEffect(() => {
        if (isSmWidth()) return;
        const bottom = showBadge || statusId === STATUS_MODEL.DENIED || pendingGateTrans ? 80 : 70;
        calculateHeight({elementRef: containerCardRef, bottom:bottom});
        calculateHeight({elementRef: containerTableRef, bottom:bottom});
        calculateHeight({elementRef: contentCardRef, bottom:bottom});
        calculateHeight({elementRef: contentTableRef, bottom:bottom});
    }, [showBadge, statusId, pendingGateTrans]);

    /**
     * Effect: Loads the entry and exit form information
     * when the component is initialized.
     */
    useEffect(() => {
        getInfo();
    }, []);

    /**
     * Retrieves the entry and exit form information by identifier.
     *
     * Behavior:
     * - Fetches form information from backend service.
     * - Updates local state with:
     *   - Form information.
     *   - Associated evidence files.
     * - Maps evidence into FILE_MODEL instances for UI handling.
     * - Displays backend error messages when request fails.
     *
     * @returns {void}
     */
    const getInfo = () => {
        dispatch(GetMaterialControlById(id)).then(
            ({
                 payload: {
                     data: { data, success, code },
                 }
             }) => {
                if (success) {
                    setItem(data);
                    const aux = data?.evidence?.map(e => FILE_MODEL.set(e));
                    if (aux) setEvidence(aux);
                } else errorMsg(code);
            }
        );
    };

    /**
     * Navigates back to the view the detail was opened from, such as the QR
     * scanner, or to the entry and exit forms main view when there is no origin.
     *
     * @returns {void}
     */
    const goBack = () => {
        const backUrl = location?.state?.backUrl;
        navigate(backUrl?.pathname || routes.entryAndExitForms.main, {state: backUrl?.state});
    };

    return {
        t,
        navigate,
        id,
        getInfo,
        DEV,
        isGuard,
        permission,
        contentCardRef,
        contentTableRef,
        containerCardRef,
        containerTableRef,
        item: detail,
        evidence,
        form,
        statusId,
        movementDirectionOption,
        movementDirectionSection,
        showBadge,
        pendingGateTrans,
        goBack
    };
};

export default GeneralEntryExitDetailsApp;
